import { useCallback, useEffect, useRef } from "react";
import {
  identityRefreshToken,
  identitySessionHandoff,
  sendIdentityTelemetry,
  type LoginSuccess,
} from "../lib/identityApi";
import { redirectToLogin } from "@speedycore/identity-client/sso";
import { CANONICAL_SESSION_MESSAGES } from "@speedycore/identity-ui";

type SilentOpts = {
  /** When the user has session HTTP cookie only — try mint a refresh_token via handoff. */
  accessToken?: string;
  refreshToken?: string | null;
  onTokens: (next: LoginSuccess) => void;
  onExpired?: () => void;
  /** Refresh interval in ms — defaults to 10 minutes. */
  intervalMs?: number;
  clientId?: "ide" | "identity_web";
  enabled?: boolean;
};

/**
 * Silent refresh — every 10 minutes.
 * Also extends the timer when there is user activity (mousemove, keydown, focus).
 * Graceful expiry → redirects to /auth/login?expired=1 with canonical copy.
 */
export function useSilentRefresh(opts: SilentOpts): {
  refreshing: boolean;
  message: string;
  forceRefresh: () => Promise<void>;
} {
  const {
    accessToken,
    refreshToken,
    onTokens,
    onExpired,
    intervalMs = 10 * 60 * 1000,
    clientId = "identity_web",
    enabled = true,
  } = opts;
  const timer = useRef<number | undefined>(undefined);
  const busy = useRef(false);
  const lastActivity = useRef<number>(Date.now());
  const stateRef = useRef<{ message: string; refreshing: boolean }>({
    message: "",
    refreshing: false,
  });
  const set = (patch: Partial<{ refreshing: boolean; message: string }>) => {
    stateRef.current = { ...stateRef.current, ...patch };
    const el = document.getElementById("sc-identity-session-state");
    if (el) {
      el.dataset.refreshing = String(stateRef.current.refreshing);
      el.textContent = stateRef.current.message;
    }
  };

  const go = useCallback(async () => {
    if (!enabled || busy.current) return;
    busy.current = true;
    set({ refreshing: true, message: CANONICAL_SESSION_MESSAGES.refreshing });
    let success = false;
    try {
      if (refreshToken) {
        const next = await identityRefreshToken(refreshToken, clientId);
        onTokens(next);
        success = true;
      } else if (accessToken || typeof window !== "undefined") {
        const handoff = await identitySessionHandoff(clientId);
        if (handoff) {
          onTokens(handoff);
          success = true;
        }
      }
    } catch (err) {
      const code =
        typeof err === "object" && err && "message" in err
          ? String((err as Error).message)
          : "token_expired";
      void sendIdentityTelemetry("identity.token_expired", {
        error_code: code.includes("Invalid") ? "invalid_credentials" : "token_expired",
      });
    } finally {
      busy.current = false;
    }
    if (!success) {
      set({ refreshing: false, message: CANONICAL_SESSION_MESSAGES.expired });
      onExpired?.();
      const current = `${window.location.origin}${window.location.pathname}${window.location.search}`;
      redirectToLogin(current, { expired: true });
      return;
    }
    set({ refreshing: false, message: "" });
  }, [enabled, refreshToken, clientId, accessToken, onTokens, onExpired]);

  useEffect(() => {
    if (!enabled) return;
    const tick = () => {
      const elapsed = Date.now() - lastActivity.current;
      if (elapsed < Math.floor(intervalMs * 0.8)) {
        timer.current = window.setTimeout(tick, Math.min(60_000, intervalMs));
        return;
      }
      void go();
      timer.current = window.setTimeout(tick, intervalMs);
    };
    timer.current = window.setTimeout(tick, intervalMs);
    const bump = () => {
      lastActivity.current = Date.now();
    };
    window.addEventListener("mousemove", bump);
    window.addEventListener("keydown", bump);
    window.addEventListener("focus", bump);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
      window.removeEventListener("mousemove", bump);
      window.removeEventListener("keydown", bump);
      window.removeEventListener("focus", bump);
    };
  }, [enabled, intervalMs, go]);

  return {
    refreshing: stateRef.current.refreshing,
    message: stateRef.current.message,
    forceRefresh: go,
  };
}
